import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowLeft, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null); // null = caricamento, false = non trovato

  useEffect(() => {
    setPost(null);
    api.get(`/posts/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(false));
  }, [slug]);

  if (post === null) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1F8296]" data-testid="blog-post-loading" />
      </main>
    );
  }

  if (post === false) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-40 text-center" data-testid="blog-post-notfound">
        <h1 className="font-display text-3xl font-semibold text-[#1A2E33]">Articolo non trovato</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1F8296] hover:text-[#238496]">
          <ArrowLeft className="h-4 w-4" /> Torna al blog
        </Link>
      </main>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.meta_description || undefined,
    image: post.featured_image_url || undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { "@type": "Organization", name: "Scuola Didattica Sicura" },
    publisher: {
      "@type": "Organization",
      name: "Scuola Didattica Sicura S.r.l.",
      logo: { "@type": "ImageObject", url: "https://www.scuoladidatticasicura.it/logo.png" },
    },
  };

  return (
    <main>
      <Seo
        title={`${post.title} | Scuola Didattica Sicura`}
        description={post.meta_description || post.title}
        path={`/blog/${post.slug}`}
        image={post.featured_image_url || "/images/hero.jpg"}
      />
      <article className="mx-auto max-w-3xl px-5 py-36 sm:py-40" data-testid="blog-post">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F8296] transition-colors hover:text-[#238496]">
          <ArrowLeft className="h-4 w-4" /> Tutti gli articoli
        </Link>

        <p className="mt-8 font-plex text-[11px] uppercase tracking-[0.24em] text-[#7A8F96]">{formatDate(post.created_at)}</p>
        <h1 className="text-balance mt-3 font-display text-3xl font-semibold leading-tight tracking-tight text-[#1A2E33] sm:text-4xl">
          {post.title}
        </h1>

        {post.keywords?.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {post.keywords.map((k) => (
              <span key={k} className="rounded-full border border-[#C9DBDF] bg-[#EFF6F7] px-3 py-1 text-[11px] font-semibold text-[#33484F]">
                {k}
              </span>
            ))}
          </div>
        )}

        {post.featured_image_url && (
          <img
            src={post.featured_image_url}
            alt={post.title}
            className="mt-8 w-full rounded-3xl border border-[#D8E5E8] object-cover"
            loading="lazy"
          />
        )}

        <div
          className="article-content mt-10"
          data-testid="blog-post-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.html) }}
        />
      </article>
    </main>
  );
}
